import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import * as React from 'react';

import { Spinner } from 'components/ui/spinner';
import { queryConfig } from 'lib/react-query';
import { StytchB2BProvider } from '@stytch/react/b2b';
import { StytchB2BUIClient } from '@stytch/vanilla-js/b2b';
import { env } from 'config/env';
import { ThemeProvider } from 'components/styles/theme-provider';
import { ErrorBoundary } from 'components/errors/error-boundary';

type AppProviderProps = {
    children: React.ReactNode;
};

// customBaseUrl routes SDK calls through the Stytch custom domain (login.withatomix.com),
// which sets intermediate/session cookies on withatomix.com. That works on app/hospital
// subdomains but fails on localhost (intermediate_session_not_found) because those cookies
// are never readable here. Cookie domain must also match the JS origin.
const isLocalhost = (() => {
    try {
        return new URL(env.APP_URL).hostname === 'localhost';
    } catch {
        return false;
    }
})();

const stytch = new StytchB2BUIClient(
    env.STYTCH_PUBLIC_TOKEN,
    isLocalhost
        ? {
              cookieOptions: {
                  path: '/',
              },
          }
        : {
              cookieOptions: {
                  path: '/',
                  domain: 'withatomix.com',
                  availableToSubdomains: true,
              },
              customBaseUrl: env.STYTCH_BASE_URL,
          },
);

export const AppProvider = ({ children }: AppProviderProps) => {
    const [queryClient] = React.useState(
        () =>
            new QueryClient({
                defaultOptions: queryConfig,
            }),
    );

    return (
        <ErrorBoundary>
            <React.Suspense
                fallback={
                    <div className="flex h-screen w-screen items-center justify-center">
                        <Spinner size="xl" />
                    </div>
                }
            >
                <StytchB2BProvider stytch={stytch}>
                    <QueryClientProvider client={queryClient}>
                        {import.meta.env.DEV && <ReactQueryDevtools />}
                        <ThemeProvider
                            defaultTheme="light"
                            storageKey="ui-theme"
                        >
                            {children}
                        </ThemeProvider>
                    </QueryClientProvider>
                </StytchB2BProvider>
            </React.Suspense>
        </ErrorBoundary>
    );
};
